import React, { ReactElement, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMount } from 'react-use';
import { Column } from '@zeroedin-tech/zi-common-ui/lib/';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib/components/toast/ToastProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import {
	AlertVariant,
	FormGroup,
	Grid,
	GridItem,
	Modal,
	ModalVariant,
} from '@patternfly/react-core';
import { SubheaderContext } from '../layout/Layout';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import FilterTableLayout from '../layout/FilterTableLayout';
import { ErrorLog, TErrorLog } from '../api/logs/ErrorLogs';
import { unixToHumanTimestamp } from '../helper/Helpers';
import { DummyPermission, useIsGranted } from '../helper/PermissionOverride';
import ZiTable, { Action } from '@zeroedin-react/zi-common/lib/components/table/ZiTable';

export default function ErrorLogs(): ReactElement {
	const { addToast } = useToast();
	const [setSubheader]: SubheaderContext = useOutletContext();
	const [tableData, setTableData] = useState<TErrorLog[]>([]);
	const [filteredTableData, setFilteredTableData] = useState<TErrorLog[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [activeErrorLog, setActiveErrorLog] = useState<TErrorLog>(ErrorLog.Default());
	const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>();

	const selectedColumns: Column<TErrorLog>[] = [
		{
			title: 'Created Date',
			columnName: 'create_date',
			customAccessor(row) {
				return unixToHumanTimestamp(row.create_date, true);
			},
			sortable: true,
		},
		{
			title: 'Error Message',
			columnName: 'data',
			customAccessor(row) {
				return JSON.stringify(row.data, null, 2).substring(0, 120) + '...';
			},
		},
		{
			title: 'Origin',
			columnName: 'origin',
			sortable: true,
		},
	];

	const actions: Action<TErrorLog, typeof DummyPermission>[] = [
		{
			name: (
				<>
					<span className="hidden">Edit</span>
					View <FontAwesomeIcon icon={faEye} />
				</>
			),
			callback: (item) => {
				setActiveErrorLog(item);
				setIsModalOpen(true);
			},
			permission: DummyPermission.EDIT,
		},
	];

	useMount(() => {
		const filterInput = document.querySelector('input[placeholder="Filter"]');
		if (filterInput) {
			filterInput.classList.add('hidden');
		}
		setSubheader(
			<PageTitleSubheader
				pageTitle="Error Logs"
				pageDescription="Manage Error Logs."
			/>
		);

		ErrorLog.GetAll()
			.then((response) => {
				setTableData(response.sort((a, b) => b.create_date - a.create_date));
				setFilteredTableData(response.sort((a, b) => b.create_date - a.create_date));
			})
			.catch(() => {
				addToast('Failed to load Error Logs.', AlertVariant.danger);
			})
			.finally(() => {
				setTableLoading(false);
			});
	});

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const updateLogsBySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.toLowerCase();
		setSearchTerm(value);

		const updatedTableData = tableData.filter(
			(x) =>
				unixToHumanTimestamp(x.create_date).includes(value) ||
				JSON.stringify(x.data, null, 2).includes(value)
		);
		setFilteredTableData(updatedTableData);
	};

	const entityTable = (
		<ZiTable<TErrorLog, typeof DummyPermission>
			ariaLabel={'Error Logs'}
			columns={selectedColumns}
			data={filteredTableData}
			caption="Error Logs"
			actions={actions}
			loading={tableLoading}
			useIsGranted={useIsGranted}
			Permission={DummyPermission}
		/>
	);

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title="Error Log Detail"
				isOpen={isModalOpen}
				onClose={handleClose}
			>
				<FormGroup>
					<Grid hasGutter>
						<GridItem span={6}>
							<b>Origin</b>
							<div>{activeErrorLog.origin}</div>
						</GridItem>
						<GridItem span={6}>
							<b>Created Date</b>
							<div>{unixToHumanTimestamp(activeErrorLog.create_date, true)}</div>
						</GridItem>
					</Grid>
					<br />
					<hr />
					<br />
					<b>Full Error Detail:</b>
					<br />
					<br />
					<pre
						className="error-json"
						style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
					>
						{JSON.stringify(activeErrorLog.data, null, 2)}
					</pre>
				</FormGroup>
			</Modal>
			<input
				placeholder="Filter Logs"
				aria-label="Filter table"
				className="pf-c-form-control pf-m-icon"
				type="text"
				width={30}
				aria-invalid="false"
				data-ouia-component-type="PF4/TextInput"
				data-ouia-safe="true"
				value={searchTerm}
				style={{
					backgroundImage:
						'url("/static/media/magnifying-glass.bfbe8ddcd88658f5dbdf314953d04cdc.svg")',
					width: '25%',
				}}
				onChange={(e) => {
					updateLogsBySearch(e);
				}}
			/>
			<React.Fragment>
				<FilterTableLayout table={entityTable} />
			</React.Fragment>
		</React.Fragment>
	);
}
